import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import AnuncioBanner from '@/components/AnuncioBanner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Trash2, Newspaper, Pin, RefreshCw, ExternalLink, Globe } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

function Noticias() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    titulo: '',
    contenido: '',
    imagen_url: '',
    enlace_original: '',
    enlace_afiliado: '',
    texto_cta: ''
  });

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news`);
      setNews(response.data);
    } catch (error) {
      console.error('Error al cargar noticias:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateNews = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/news/generate-manual`);
      alert(`✅ ${response.data.message}`);
      fetchNews();
    } catch (error) {
      alert('Error al generar noticias: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/news`, formData);
      fetchNews();
      resetForm();
      setDialogOpen(false);
    } catch (error) {
      alert('Error al crear noticia');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta noticia?')) return;
    
    try {
      await axios.delete(`${API}/news/${id}`);
      fetchNews();
    } catch (error) {
      console.error('Error al eliminar:', error);
    }
  };

  const handleTogglePin = async (id) => {
    try {
      await axios.put(`${API}/news/${id}/pin`);
      fetchNews();
    } catch (error) {
      alert('Error al anclar/desanclar noticia');
    }
  };

  const resetForm = () => {
    setFormData({
      titulo: '',
      contenido: '',
      imagen_url: '',
      enlace_original: '',
      enlace_afiliado: '',
      texto_cta: ''
    });
  };

  // Filtrar noticias
  const noticiasInternas = news.filter(n => n.fuente === 'interna');
  const noticiasExternas = news.filter(n => n.fuente === 'externa');

  return (
    <div className="min-h-screen bg-dark-mk">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        {/* Banner de publicidad */}
        <AnuncioBanner />
        
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gold mb-2" data-testid="noticias-title">
              Noticias y Actualidad
            </h1>
            <p className="text-gray-400">Mantente al día con lo que pasa en el Box y en el mundo fitness</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <Tabs defaultValue="internas" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-gray-800 mb-8">
              <TabsTrigger value="internas" className="data-[state=active]:bg-gold data-[state=active]:text-black text-gray-400">
                Noticias MK (Internas)
              </TabsTrigger>
              <TabsTrigger value="externas" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-gray-400">
                Actualidad Fitness (IA)
              </TabsTrigger>
            </TabsList>

            <TabsContent value="internas" className="space-y-6">
                {/* Botón Crear (Solo Entrenador) */}
                {user?.role === 'entrenador' && (
                    <div className="flex justify-end mb-4">
                        <Dialog open={dialogOpen} onOpenChange={(open) => {
                            setDialogOpen(open);
                            if (!open) resetForm();
                        }}>
                            <DialogTrigger asChild>
                            <Button className="btn-primary-mk w-full md:w-auto" data-testid="btn-add-news">
                                <Plus className="mr-2 h-4 w-4" /> Crear Noticia Manual
                            </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-2xl">
                            <DialogHeader>
                                <DialogTitle className="text-gold">Nueva Noticia del Club</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                <Label htmlFor="titulo">Título</Label>
                                <Input
                                    id="titulo"
                                    value={formData.titulo}
                                    onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                                    required
                                    className="bg-gray-800 border-gray-700 text-white"
                                    placeholder="Ej: Cambio de horario Navidad"
                                />
                                </div>
                                <div className="space-y-2">
                                <Label htmlFor="contenido">Contenido</Label>
                                <Textarea
                                    id="contenido"
                                    value={formData.contenido}
                                    onChange={(e) => setFormData({ ...formData, contenido: e.target.value })}
                                    required
                                    className="bg-gray-800 border-gray-700 min-h-[150px]"
                                    placeholder="Escribe aquí el cuerpo de la noticia..."
                                />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="imagen_url">URL Imagen (Opcional)</Label>
                                        <Input
                                            id="imagen_url"
                                            value={formData.imagen_url}
                                            onChange={(e) => setFormData({ ...formData, imagen_url: e.target.value })}
                                            className="bg-gray-800 border-gray-700 text-white"
                                            placeholder="https://..."
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="enlace_original">Enlace Externo (Opcional)</Label>
                                        <Input
                                            id="enlace_original"
                                            value={formData.enlace_original}
                                            onChange={(e) => setFormData({ ...formData, enlace_original: e.target.value })}
                                            className="bg-gray-800 border-gray-700 text-white"
                                            placeholder="https://..."
                                        />
                                    </div>
                                </div>
                                <Button type="submit" className="w-full btn-primary-mk mt-4">
                                Publicar
                                </Button>
                            </form>
                            </DialogContent>
                        </Dialog>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {noticiasInternas.map((article) => (
                    <NoticiaCard 
                        key={article.id} 
                        article={article} 
                        user={user} 
                        onDelete={handleDelete} 
                        onPin={handleTogglePin} 
                    />
                    ))}
                    {noticiasInternas.length === 0 && (
                        <div className="col-span-full text-center py-12 bg-gray-800/30 rounded-lg border border-gray-700">
                            <Newspaper className="w-12 h-12 mx-auto text-gray-600 mb-2" />
                            <p className="text-gray-400">No hay noticias internas publicadas.</p>
                        </div>
                    )}
                </div>
            </TabsContent>

            <TabsContent value="externas" className="space-y-6">
                {user?.role === 'entrenador' && (
                    <div className="flex justify-end mb-4">
                        <Button 
                            onClick={handleGenerateNews} 
                            disabled={generating}
                            className="bg-purple-600 hover:bg-purple-700 text-white w-full md:w-auto" 
                        >
                            <RefreshCw className={`mr-2 h-4 w-4 ${generating ? 'animate-spin' : ''}`} /> 
                            {generating ? 'Generando Contenido...' : 'Generar Noticias IA'}
                        </Button>
                    </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {noticiasExternas.map((article) => (
                    <NoticiaCard 
                        key={article.id} 
                        article={article} 
                        user={user} 
                        onDelete={handleDelete} 
                        onPin={handleTogglePin} 
                    />
                    ))}
                    {noticiasExternas.length === 0 && (
                        <div className="col-span-full text-center py-12 bg-gray-800/30 rounded-lg border border-gray-700">
                            <Globe className="w-12 h-12 mx-auto text-gray-600 mb-2" />
                            <p className="text-gray-400">No hay noticias de actualidad generadas.</p>
                        </div>
                    )}
                </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}

// Componente auxiliar para la tarjeta de noticia (para no repetir código)
function NoticiaCard({ article, user, onDelete, onPin }) {
    return (
        <Card className={`card-mk flex flex-col h-full ${article.anclada ? 'ring-2 ring-gold' : ''}`}>
            {article.imagen_url && (
                <div className="w-full h-48 overflow-hidden rounded-t-lg relative shrink-0">
                <img
                    src={article.imagen_url}
                    alt={article.titulo}
                    className="w-full h-full object-cover transition-transform hover:scale-105"
                />
                {article.anclada && (
                    <div className="absolute top-2 left-2 bg-gold text-gray-900 px-3 py-1 rounded-full font-bold flex items-center gap-1 text-xs">
                    <Pin className="w-3 h-3" /> DESTACADA
                    </div>
                )}
                </div>
            )}
            <CardHeader className="pb-2">
                <p className="text-gray-500 text-xs mb-1 uppercase tracking-wide">
                    {format(new Date(article.fecha), "d MMMM yyyy", { locale: es })}
                </p>
                <CardTitle className="text-white text-xl leading-tight">
                    {article.titulo}
                </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col space-y-4 pt-0">
                <p className="text-gray-300 text-sm line-clamp-4 flex-1">
                    {article.contenido}
                </p>
                
                <div className="mt-auto pt-4 space-y-3">
                    {article.enlace_original && (
                        <a 
                            href={article.enlace_original}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-gold border border-gray-700 px-4 py-2 rounded transition-colors text-sm w-full"
                        >
                            <ExternalLink className="h-4 w-4" />
                            Leer más
                        </a>
                    )}

                    {user?.role === 'entrenador' && (
                        <div className="flex gap-2 border-t border-gray-800 pt-3">
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => onPin(article.id)}
                                className={`flex-1 h-8 text-xs ${article.anclada ? 'text-gold' : 'text-gray-400 hover:text-white'}`}
                            >
                                <Pin className="h-3 w-3 mr-1" /> {article.anclada ? 'Desanclar' : 'Anclar'}
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => onDelete(article.id)}
                                className="flex-1 h-8 text-xs text-red-400 hover:text-red-300 hover:bg-red-900/20"
                            >
                                <Trash2 className="h-3 w-3 mr-1" /> Eliminar
                            </Button>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

export default Noticias;